// src/lib/ExtendedWarrantyService.js
import API_CONFIG from "./ApiConfig";

const ExtendedWarrantyService = {
  /**
   * Upload a warranty document file to the server
   *
   * @param {File} file - The file to upload
   * @param {string} firebaseToken - Firebase authentication token
   * @returns {Promise<string>} - URL of the uploaded file
   */
  uploadWarrantyDocument: async (file, firebaseToken) => {
    try {
      // Convert file to base64
      const base64File = await fileToBase64(file);

      // Prepare payload for file upload
      const payload = {
        name: file.name,
        deviceManufacturer: "Web",
        deviceModel: "Browser",
        deviceOS: navigator.userAgent,
        deviceIdentifier: crypto.randomUUID(),
        deviceType: "Web",
        latitude: 0,
        longitude: 0,
        file: base64File,
        documentType: "EXTENDED_WARRANTY", // Specific type for warranty docs
      };

      const response = await fetch(`${API_CONFIG.BASE_URL}/api/file/upload`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-token": firebaseToken,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "File upload failed");
      }

      const data = await response.json();
      return data.fileUrl;
    } catch (error) {
      console.error("Error uploading warranty document:", error);
      throw error;
    }
  },

  /**
   * Submit warranty policy check-in data
   *
   * @param {string} fileUrl - URL of the uploaded file
   * @param {string} firebaseToken - Firebase authentication token
   * @returns {Promise<Object>} - API response
   */
  submitWarrantyPolicyCheckin: async (fileUrl, firebaseToken) => {
    try {
      const payload = {
        userId: "noop",
        warrantyType: "EXTENDED",
        files: [
          {
            url: fileUrl,
            documentType: "EXTENDED_WARRANTY_POLICY",
            type: "DOCUMENT",
          },
        ],
        latitude: 0.0,
        longitude: 0.0,
      };

      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/users/checkin/extended-warranty`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-user-token": firebaseToken,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Warranty policy check-in failed");
      }

      return await response.json();
    } catch (error) {
      console.error("Error submitting warranty policy check-in:", error);
      throw error;
    }
  },

  /**
   * Submit mileage check-in with photos
   *
   * @param {File} odometerPhoto - Odometer photo file
   * @param {File} licensePhoto - License plate photo file
   * @param {string} firebaseToken - Firebase authentication token
   * @returns {Promise<Object>} - API response
   */
  submitMileageCheckin: async (odometerPhoto, licensePhoto, firebaseToken) => {
    try {
      // Upload both photos first
      const odometerUrl = await ExtendedWarrantyService.uploadWarrantyDocument(
        odometerPhoto,
        firebaseToken
      );
      const licenseUrl = await ExtendedWarrantyService.uploadWarrantyDocument(
        licensePhoto,
        firebaseToken
      );

      // Submit mileage check-in with both photo URLs
      const payload = {
        userId: "noop",
        checkinType: "MILEAGE",
        files: [
          {
            url: odometerUrl,
            documentType: "ODOMETER_READING",
            type: "PHOTO",
          },
          {
            url: licenseUrl,
            documentType: "LICENSE_PLATE",
            type: "PHOTO",
          },
        ],
        latitude: 0.0,
        longitude: 0.0,
      };

      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/users/checkin/mileage`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-user-token": firebaseToken,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Mileage check-in failed");
      }

      return await response.json();
    } catch (error) {
      console.error("Error submitting mileage check-in:", error);
      throw error;
    }
  },

  /**
   * Process complete warranty policy upload workflow
   *
   * @param {File} file - The file to upload
   * @param {string} firebaseToken - Firebase authentication token
   * @returns {Promise<Object>} - Result with upload and check-in information
   */
  processWarrantyPolicyDocument: async (file, firebaseToken) => {
    try {
      console.log("Processing warranty policy document", file);

      // Step 1: Upload the file and get the URL
      const fileUrl = await ExtendedWarrantyService.uploadWarrantyDocument(
        file,
        firebaseToken
      );

      // Step 2: Submit the warranty policy check-in with the file URL
      const result = await ExtendedWarrantyService.submitWarrantyPolicyCheckin(
        fileUrl,
        firebaseToken
      );

      return {
        success: true,
        result,
        fileUrl,
        message:
          "Warranty document uploaded and check-in submitted successfully",
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to process warranty policy document",
      };
    }
  },

  /**
   * Process complete mileage check-in workflow
   *
   * @param {File} odometerPhoto - Odometer photo file
   * @param {File} licensePhoto - License plate photo file
   * @param {string} firebaseToken - Firebase authentication token
   * @returns {Promise<Object>} - Result with check-in information
   */
  processMileageCheckin: async (odometerPhoto, licensePhoto, firebaseToken) => {
    try {
      console.log("Processing mileage check-in", {
        odometerPhoto,
        licensePhoto,
      });

      // Submit the mileage check-in with both photos
      const result = await ExtendedWarrantyService.submitMileageCheckin(
        odometerPhoto,
        licensePhoto,
        firebaseToken
      );

      return {
        success: true,
        result,
        message: "Mileage check-in submitted successfully",
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to process mileage check-in",
      };
    }
  },

  createFlow: async (token) => {
    const response = await fetch(
      `${API_CONFIG.BASE_URL}/api/extended-warranty/flow`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-token": token,
        },
        body: JSON.stringify({}),
      }
    );
    return await response.json();
  },

  getFlow: async (token) => {
    const response = await fetch(
      `${API_CONFIG.BASE_URL}/api/extended-warranty/flow`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-user-token": token,
        },
      }
    );
    return await response.json();
  },

  /**
   * Check if the flow has a policy checkin
   */
  hasExistingPolicyCheckin: (flowData) => {
    return !!(
      flowData &&
      flowData.policyCheckInId &&
      flowData.insurancePolicyCheckinDto
    );
  },

  /**
   * Get policy checkin details from flow
   */
  getPolicyCheckinFromFlow: (flowData) => {
    if (!ExtendedWarrantyService.hasExistingPolicyCheckin(flowData)) {
      return null;
    }

    return {
      id: flowData.insurancePolicyCheckinDto.id,
      status: flowData.insurancePolicyCheckinDto.checkInStatus,
      checkedInAt: flowData.insurancePolicyCheckinDto.checkedInAt,
      policyType: flowData.insurancePolicyCheckinDto.policyType,
      files: flowData.insurancePolicyCheckinDto.files || [],
    };
  },
};

/**
 * Helper function to convert a file to base64
 *
 * @param {File} file - The file to convert
 * @returns {Promise<string>} - Base64 representation of the file
 */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
      const base64 = reader.result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

export default ExtendedWarrantyService;
